/**
 * RP4 — SceneContent local refs + R3F passthrough hook.
 *
 * Owns every ref the SceneContent component needs for the bulk-load,
 * layer visibility, selection texture, hover bridge, frame-loops, and
 * pointer-handler effects. Also publishes R3F's `camera`, `invalidate`,
 * and `spatialIndex` into the outer Scene3DStage refs so the outer
 * pointer/wheel handlers can read them without crossing the R3F
 * portal.
 *
 * # Why a single hook
 *
 * The pre-RP4 SceneContent body declared 13 useRef + 3 small
 * passthrough useEffects inline. Bundling them gives the orchestrator
 * a single hook call with one ref bundle returned to the caller.
 *
 * # Opt 8 — Map consolidation (Phase 2)
 *
 * Pre-consolidation the hook kept eight parallel Maps
 * (`meshesRef` / `materialsRef` / `selectionTexturesRef` /
 * `bucketHandlesRef` per bucket, plus `fillMeshesRef` / `textMeshesRef`
 * / `entityToTextureSlotRef` / `lodStateRef` per entity). Every bucket
 * mutation required four `Map.set` calls; every bucket teardown four
 * `Map.delete` calls; every entity render four entries scattered across
 * unrelated Maps. The eight Map headers ate ≈ 320 bytes per
 * SceneContent instance and forced four hash-table look-ups for the
 * single logical operation "give me the render state of bucket X".
 *
 * Opt 8 collapses these into two grouped Maps:
 *   - `bucketsRef` — `Map<MeshKeyId, BucketState>` per bucket
 *   - `entityRendersRef` — `Map<string, EntityRender>` per entity
 *
 * Each value is a single object that holds every per-bucket /
 * per-entity field. Atomic add/remove/lookup; halved Map headers; no
 * change to render semantics — `BucketState`, `EntityRender` are pure
 * grouping types declared in `SceneRefsTypes.ts`.
 */

import { useEffect, useRef, type MutableRefObject } from "react";
import * as THREE from "three";

import type { RenderableEntity, RendererHandle, SpatialIndex } from "../types";
import type {
  CameraSnapshot,
  VisibleEntityGroups,
} from "../lod";
import type { VisibleSetCache } from "../state/visibleSetCache";
import { createVisibleSetCache } from "../state/visibleSetCache";

import type {
  BucketState,
  EntityRender,
  EntityLiftRequest,
} from "./SceneRefsTypes";
import type { MeshKeyId } from "../batchedLayer";
import type {
  LinetypeResolutionMap,
  LayerLinetypeMap,
} from "../rendering/linetypeResolve";
import type { CameraFitDocState } from "./sceneBulkLoadHelpers";
import {
  createSpaceLayerCensusCache,
  type SpaceLayerCensusCache,
} from "./spaceLayerCensusCache";

/**
 * PR-LT3 variant A (A2) — GLOBAL dash-scale sysvars carried on the relift ref.
 * Structurally identical to `Scene3DStageProps["linetypeSysvars"]`; declared
 * inline here to avoid a Scene3DStageProps import cycle into the refs module.
 */
export type LinetypeSysvarsRefValue =
  | { LTSCALE: number; MSLTSCALE: number; PSLTSCALE: number }
  | undefined;

interface UseSceneContentRefsArgs {
  camera: THREE.Camera;
  invalidate: () => void;
  liveCameraRef: MutableRefObject<THREE.Camera | null>;
  invalidateRef: MutableRefObject<(() => void) | null>;
  spatialIndexRefForHover: MutableRefObject<SpatialIndex | null>;
  handleRef: MutableRefObject<RendererHandle | null>;
  // entities prop included so the spatial-index publish effect re-runs
  // when the entity feed mutates (handle.spatialIndex may swap).
  entitiesIdentity: unknown;
  /**
   * Codex PR #784 P1 — optional OUTER-owned map adopted as
   * `multileaderBlockChildrenRef` (one Map instance, never copied): the
   * paper-space viewport orchestrator in `Scene3DStage` projects the
   * render-only satellites' layers into its VP visibility sets from this
   * exact map (`scene/satelliteLayerProjection.ts`). Absent ⇒ a private map
   * (unit tests / callers without a VP pass).
   */
  sharedRenderSatellitesRef?: MutableRefObject<Map<string, RenderableEntity[]>>;
  /**
   * Codex PR #784 round 3 P1 #2 — optional OUTER-owned revision counter of
   * that map, adopted as `renderSatellitesVersionRef` (see the bundle field).
   * Absent ⇒ a private counter.
   */
  sharedRenderSatellitesVersionRef?: MutableRefObject<number>;
  /**
   * Codex PR #784 round 3 P2 — optional OUTER-owned model / paper layer
   * census (`spaceLayerCensusCache.ts`) adopted as `spaceLayerCensusRef`, so
   * the `FrameLoops` relift path unions a relifted parent's satellite layers
   * into the SAME census the paper-space orchestrator gates on. Absent ⇒ a
   * private cache (unit tests / callers without a VP pass).
   */
  sharedSpaceLayerCensusRef?: MutableRefObject<SpaceLayerCensusCache>;
  /**
   * Codex PR #784 round 4 F2 — optional OUTER-owned GLOBAL off / frozen layer
   * set adopted as `hiddenLayersRef` (ONE Set instance, never copied), so the
   * stage's pointer-move hover hit-test reads the EXACT set
   * `LayerVisibilityEffect` republishes here and the click path already uses.
   * Absent ⇒ a private set (unit tests / callers without the stage binder).
   */
  sharedHiddenLayersRef?: MutableRefObject<Set<string>>;
}

export interface SceneContentRefBundle {
  /**
   * Opt 8 — consolidated per-bucket render state. Replaces the four
   * pre-consolidation Maps (`meshesRef`, `materialsRef`,
   * `selectionTexturesRef`, `bucketHandlesRef`).
   */
  bucketsRef: MutableRefObject<Map<MeshKeyId, BucketState>>;
  /**
   * Opt 8 — consolidated per-entity render state. Replaces the four
   * pre-consolidation Maps (`fillMeshesRef`, `textMeshesRef`,
   * `entityToTextureSlotRef`, `lodStateRef`).
   */
  entityRendersRef: MutableRefObject<Map<string, EntityRender>>;
  textBuildTokenRef: MutableRefObject<number>;
  lockedLayersRef: MutableRefObject<Set<string>>;
  hiddenLayersRef: MutableRefObject<Set<string>>;
  prevCameraSnapshotRef: MutableRefObject<CameraSnapshot | null>;
  lodPointsCloudRef: MutableRefObject<THREE.Points | null>;
  /**
   * Codex PR #784 round-12 P2 — the ATTRIBUTED viewport point-proxy clouds
   * (`scene/viewportPointProxies.ts`), one per (requesting-viewport set,
   * layer). Rebuilt with the main cloud on every camera-changed frame and torn
   * down with it (`sceneTeardown.ts disposeLodCloud`).
   */
  lodPointProxyCloudsRef: MutableRefObject<THREE.Points[]>;
  lodGroupsRef: MutableRefObject<VisibleEntityGroups>;
  visibleSetCacheRef: MutableRefObject<VisibleSetCache | null>;
  /**
   * Codex P3 follow-up / Item C (2026-05-19) — per-frame viewport-cull
   * re-lift tracking.
   *
   * Tracks the set of entity ids currently materialised in
   * `BatchedLayer` (the renderer's "what is actually on the GPU right
   * now"). Populated by `runSceneBulkLoad` after the initial lift;
   * mutated incrementally by the `FrameLoops` viewport-cull pass as
   * the user pans / zooms and entities cross the "would benefit from
   * full-geometry" LOD boundary.
   *
   * Without this Set, the cache-direct path's LOD pre-classify
   * (Codex P3) was effectively one-shot — entities dropped as "skip"
   * at fit-to-extents stayed dropped forever, so zoom-in could not
   * surface sub-pixel detail. With it, the per-frame pass can diff
   * the new visible set against the lifted set and add / remove
   * entities incrementally.
   */
  liftedEntityIdsRef: MutableRefObject<Set<string>>;
  /**
   * Codex P3 follow-up / Item C (2026-05-19) — on-demand entity
   * resolver bound to the bulk-load's `cache` + `buildPreview` +
   * `adapterCtxBase` context.
   *
   * `runSceneBulkLoad` constructs the resolver after wiring its
   * adapter context (a closure over `liftRenderableForId` so the
   * FrameLoops viewport-cull pass can materialise a single entity
   * without re-running the bulk-load pipeline). Returns `null` when
   * the id is not present in the spatial index / metadata cache, or
   * when the resolver hasn't been wired yet (initial mount).
   *
   * The resolver is reset to `null` on each new bulk-load so a stale
   * pass from a prior document can't lift entities into the current
   * scene.
   */
  entityResolverRef: MutableRefObject<
    | ((id: string, request?: EntityLiftRequest) => RenderableEntity | null)
    | null
  >;
  /**
   * S9b — parent MLEADER id → its block-content flattened child renderables.
   *
   * Block-content children are render-only satellites: they are drawn as
   * first-class bucket / fill / text entities but are NEVER placed in the
   * spatial index (the MLEADER is the single pickable entity). They therefore
   * must NOT be tracked in `liftedEntityIdsRef` — the per-frame viewport-cull
   * diffs the lifted set against a spatial-index-derived visible set, so a
   * child (absent from the index) would be zero-filled on the first cull frame
   * and could never be re-lifted (no stub for the resolver to hit).
   *
   * This map lets the cull treat the children as satellites of the parent:
   * when a parent MLEADER is culled the pass removes its children in lockstep;
   * when the parent is re-lifted the pass re-expands + re-adds them. Populated
   * by `finishSceneBulkLoad` (from the un-expanded top-level renderables) and
   * maintained incrementally by the `FrameLoops` viewport-cull pass. Cleared at
   * the top of every fresh bulk-load.
   */
  multileaderBlockChildrenRef: MutableRefObject<Map<string, RenderableEntity[]>>;
  /**
   * Codex PR #784 round 3 P1 #2 — REVISION of `multileaderBlockChildrenRef`'s
   * contents: `runSceneBulkLoad` bumps it when it clears the map and
   * `finishSceneBulkLoad` when it repopulates it (sync AND async lift paths).
   * The paper-space viewport orchestrator compares it frame-to-frame — the
   * same channel as `geometryVersion` — so its model/paper layer census is
   * recomputed once the async lift has populated the satellites
   * (`scene/spaceLayerCensusCache.ts`). Per-frame relift edits to the map
   * (`FrameLoops` Pass 1 delete / Pass 2 set) deliberately do NOT bump it —
   * a bump recomputes the census over the whole spatial index, and Pass 2
   * runs on every pan / zoom frame that relifts a parent. The bulk-load does
   * NOT guarantee that every INSERT / MLEADER was lifted before Pass 2 sees
   * it — since Codex PR #784 round-8 P2 removed the `Block` exemption from
   * `isAlwaysLiftedStubType` (`lod/lodPolicy.ts`), a sub-pixel-at-fit INSERT
   * is lifted for the FIRST time by Pass 2 (resolver fallback to the metadata
   * stub + Pass-2 direct-mesh emission make that lift complete).
   * So Pass 2 instead UNIONS a relifted parent's satellite layer chains into
   * the census (`spaceLayerCensusRef`, `SpaceLayerCensusCache.addSatellites`
   * — O(#children), monotonic, and safe because the census only ever hides).
   */
  renderSatellitesVersionRef: MutableRefObject<number>;
  /**
   * Codex PR #784 round 3 P2 — the model / paper layer census the paper-space
   * orchestrator gates on, shared with `FrameLoops` for the relift union
   * described above (see `renderSatellitesVersionRef`). Adopted from the stage
   * when supplied (`sharedSpaceLayerCensusRef`), else a private cache.
   */
  spaceLayerCensusRef: MutableRefObject<SpaceLayerCensusCache>;
  /**
   * FIX-3 (Codex P2) — the per-pass LTYPE resolution map captured at
   * bulk-load time. The FrameLoops relift path reads it to build a NEW
   * linetype bucket key (same dash fidelity as the bulk-load path). An
   * empty map resolves every non-sentinel name through the mm web
   * registry — the safe fallback before any bulk-load wired it.
   */
  linetypeResolutionRef: MutableRefObject<LinetypeResolutionMap>;
  /**
   * FIX-1 (Codex P2) — the per-pass per-layer LTYPE map captured at
   * bulk-load time. The FrameLoops relift path reads it so a `ByLayer`
   * bucket it materialises on demand resolves its dash pattern through the
   * layer's assigned linetype (same fidelity as the bulk-load path).
   */
  layerLinetypeMapRef: MutableRefObject<LayerLinetypeMap>;
  /**
   * PR-LT3 variant A (A2) — per-pass GLOBAL dash-scale sysvars captured at
   * bulk-load time. The FrameLoops relift path reads it so a bucket it
   * materialises on demand gets the same `dashScale` as the bulk-load path.
   */
  linetypeSysvarsRef: MutableRefObject<LinetypeSysvarsRefValue>;
  /**
   * Issue #225 + #235 follow-up (@codex P2 ×2) — the per-document camera
   * auto-fit claim (`null` before the first bulk-load pass ever).
   *
   * # Why a ref, not module-state
   *
   * Pre-fix bug: every bulk-load pass re-ran
   * `fitOrthographicCameraToRenderables`, which clobbers
   * `ortho.position`, frustum extents and `ortho.zoom = 1`. The reset
   * happened on **every** scene rebuild — wheel-zoom (which mutates
   * `ortho.zoom`) was wiped on the next render, the camera snapped back
   * to the bounds-fit centre, and freshly-drawn primitives appeared
   * tiny because the frustum was re-fit to a much larger scene AABB.
   *
   * AutoCAD never auto-zooms when the user adds an entity to an
   * already-open drawing — fit-to-extents is an explicit ZOOM E
   * command. We replicate that with a per-document claim: the fit fires
   * once per `geometryDocument.id`, the first time that document's
   * content is actually renderable. After that any zoom/pan change is
   * driven by user input via the camera API.
   *
   * Module-scoped `let` would share state across multiple Scene3DStage
   * instances (split-screen viewports, hot-reload, tests) and break
   * isolation — a per-instance ref keeps the claim local.
   *
   * # Why a document claim, not a lifetime latch
   *
   * The pre-#225 one-shot latch (`cameraEverInitializedRef`) was never
   * reset, and `<SceneContent>` has no doc-keyed React `key`, so it
   * persisted across document loads (open / reset / new). That made the
   * auto-fit fire ONLY for the first document of a session: loading a
   * second drawing left the latch `true`, the fit block was skipped, and
   * the new drawing rendered off-screen until the user ran ZOOM E.
   *
   * Keying the gate on the document claim fixes that: `runSceneBulkLoad`
   * fits whenever the current `geometryDocument.id` differs from the
   * claimed one, then advances the claim. Edits keep the same id
   * (geometry reducers spread the document), so they never re-fit —
   * matching AutoCAD, where opening a drawing zooms to extents but
   * adding an entity to the open drawing does not. The claim's
   * `contentSeen` flag additionally defers a streamed document's one fit
   * past its 0-renderable pre-pass (#235 follow-up @codex P2) — see the
   * `CameraFitDocState` JSDoc in `sceneBulkLoadHelpers.ts` for the full
   * state machine.
   */
  cameraFitDocStateRef: MutableRefObject<CameraFitDocState | null>;
  /**
   * Phase 2 Wave 2 Task 4 — tracker for batched-INSERT InstancedMeshes
   * built by `materializeInstancedBlockInsertMesh`. Lives alongside
   * `bucketsRef` so dispose paths can free it on bulkLoad + unmount.
   */
  instancedBlockMeshesRef: MutableRefObject<Array<THREE.InstancedMesh>>;
}

/**
 * Build the SceneContent ref bundle and run the three R3F passthrough
 * effects (camera, invalidate, spatial index publish).
 */
export function useSceneContentRefs(
  args: UseSceneContentRefsArgs,
): SceneContentRefBundle {
  // ── R3F passthrough effects ──────────────────────────────────
  // PR-V0b — expose live camera + spatial index to outer Scene3DStage
  // hooks (out-of-Canvas pointer/wheel handlers, camera api adapter).
  useEffect(() => {
    args.liveCameraRef.current = args.camera;
    return () => {
      if (args.liveCameraRef.current === args.camera)
        args.liveCameraRef.current = null;
    };
  }, [args.camera, args.liveCameraRef]);
  useEffect(() => {
    args.invalidateRef.current = args.invalidate;
    return () => {
      if (args.invalidateRef.current === args.invalidate)
        args.invalidateRef.current = null;
    };
  }, [args.invalidate, args.invalidateRef]);
  useEffect(() => {
    const handle = args.handleRef.current;
    if (handle) args.spatialIndexRefForHover.current = handle.spatialIndex;
    return () => {
      args.spatialIndexRefForHover.current = null;
    };
  }, [args.handleRef, args.spatialIndexRefForHover, args.entitiesIdentity]);

  // ── Refs owned by SceneContent ───────────────────────────────
  // Opt 8 — two consolidated Maps replace the eight pre-consolidation
  // *Refs. See `SceneRefsTypes.ts` for the field layout of each value.
  const bucketsRef = useRef<Map<MeshKeyId, BucketState>>(new Map());
  const entityRendersRef = useRef<Map<string, EntityRender>>(new Map());
  const textBuildTokenRef = useRef(0);
  const lockedLayersRef = useRef<Set<string>>(new Set());
  // Codex PR #784 round 4 F2 — adopt the stage-owned set when supplied (see
  // `UseSceneContentRefsArgs.sharedHiddenLayersRef`); the hook is always
  // called, so the choice is a plain expression, never a conditional hook.
  const ownHiddenLayersRef = useRef<Set<string>>(new Set());
  const hiddenLayersRef = args.sharedHiddenLayersRef ?? ownHiddenLayersRef;
  const prevCameraSnapshotRef = useRef<CameraSnapshot | null>(null);
  const lodPointsCloudRef = useRef<THREE.Points | null>(null);
  const lodPointProxyCloudsRef = useRef<THREE.Points[]>([]);
  const lodGroupsRef = useRef<VisibleEntityGroups>({
    full: [],
    bboxLine: [],
    point: [],
    skip: [],
  });
  // Lazy-init the cache on first render (cannot use useState because we
  // need a ref-stable identity; null sentinel + populate-on-first-pass
  // keeps the cache alive for the component lifetime).
  const visibleSetCacheRef = useRef<VisibleSetCache | null>(null);
  if (visibleSetCacheRef.current === null) {
    visibleSetCacheRef.current = createVisibleSetCache({
      maxEntries: 25_000,
    });
  }
  // Issue #225 + #235 follow-up — per-document auto-fit claim. Records
  // the `geometryDocument.id` the most recent bulk-load pass rendered
  // (+ whether that document has shown content yet) so a NEW document
  // loaded in the same session re-fits and a streamed document's fit
  // survives its 0-renderable pre-pass. See JSDoc above.
  const cameraFitDocStateRef = useRef<CameraFitDocState | null>(null);
  // Phase 2 Wave 2 Task 4 — InstancedMesh tracker for batched INSERTs.
  // Mutated in place by `runSceneBulkLoad`; disposed by
  // `disposeSceneBuckets` / `disposeSceneOnUnmount` via SceneRegistries.
  const instancedBlockMeshesRef = useRef<Array<THREE.InstancedMesh>>([]);
  // Codex P3 follow-up / Item C (2026-05-19) — per-frame viewport-cull
  // re-lift tracking. Populated by `runSceneBulkLoad` post-lift;
  // mutated by `FrameLoops` viewport-cull pass on camera-driven LOD
  // boundary crossings. See SceneContentRefBundle JSDoc for details.
  const liftedEntityIdsRef = useRef<Set<string>>(new Set());
  // S9b — parent MLEADER id → block-content flattened children. See the
  // SceneContentRefBundle JSDoc: children are render-only satellites tracked
  // here (not in `liftedEntityIdsRef`) so the viewport-cull can add / remove
  // them in lockstep with their parent MLEADER.
  const ownRenderSatellitesRef = useRef<Map<string, RenderableEntity[]>>(
    new Map(),
  );
  // Codex PR #784 P1 — adopt the outer-owned map when the stage supplies one
  // (see `UseSceneContentRefsArgs.sharedRenderSatellitesRef`); the hook is
  // always called, so the choice is a plain expression, never a conditional
  // hook.
  const multileaderBlockChildrenRef =
    args.sharedRenderSatellitesRef ?? ownRenderSatellitesRef;
  // Codex PR #784 round 3 P1 #2 — the map's revision counter, adopted from the
  // stage the same way (see the bundle field's JSDoc).
  const ownRenderSatellitesVersionRef = useRef<number>(0);
  const renderSatellitesVersionRef =
    args.sharedRenderSatellitesVersionRef ?? ownRenderSatellitesVersionRef;
  // Codex PR #784 round 3 P2 — the layer census, adopted from the stage the
  // same way (see the bundle field's JSDoc).
  const ownSpaceLayerCensusRef = useRef<SpaceLayerCensusCache>(
    createSpaceLayerCensusCache(),
  );
  const spaceLayerCensusRef =
    args.sharedSpaceLayerCensusRef ?? ownSpaceLayerCensusRef;
  // Codex PR #784 round 11 — the resolver takes an optional per-id lift REQUEST
  // (`emitScale` from the requesting viewport's model scale, `proxyOnly` for the
  // non-expanding point proxy). Declared wide HERE, where the ref is created, so
  // the contract is visible at the declaration and not only at the assignment in
  // `finishSceneBulkLoad`.
  const entityResolverRef = useRef<
    | ((id: string, request?: EntityLiftRequest) => RenderableEntity | null)
    | null
  >(null);
  // FIX-3 (Codex P2) — the per-pass LTYPE resolution map, captured at
  // bulk-load time so the FrameLoops relift path can materialise a NEW
  // linetype bucket key with the same dash fidelity as the bulk-load
  // path (same source as `buildBucketMeshes`). Reset to an empty map on
  // each fresh bulk-load (a stale doc's custom LTYPEs must not leak).
  const linetypeResolutionRef = useRef<LinetypeResolutionMap>(new Map());
  // FIX-1 — per-pass per-layer LTYPE map (lower-cased layer → linetype NAME),
  // captured at bulk-load time so the FrameLoops relift path resolves a
  // `ByLayer` bucket through the layer's assigned linetype. Reset each pass.
  const layerLinetypeMapRef = useRef<LayerLinetypeMap>(new Map());
  // PR-LT3 variant A (A2) — per-pass linetype sysvars (LTSCALE / MSLTSCALE /
  // PSLTSCALE), captured at bulk-load time so the FrameLoops relift path
  // materialises a new bucket with the same GLOBAL dash scale. `undefined`
  // until the first bulk-load publishes it (→ all-1, pre-A2 behaviour).
  const linetypeSysvarsRef = useRef<LinetypeSysvarsRefValue>(undefined);

  return {
    bucketsRef,
    entityRendersRef,
    textBuildTokenRef,
    lockedLayersRef,
    hiddenLayersRef,
    prevCameraSnapshotRef,
    lodPointsCloudRef,
    lodPointProxyCloudsRef,
    lodGroupsRef,
    visibleSetCacheRef,
    cameraFitDocStateRef,
    instancedBlockMeshesRef,
    liftedEntityIdsRef,
    multileaderBlockChildrenRef,
    renderSatellitesVersionRef,
    spaceLayerCensusRef,
    entityResolverRef,
    linetypeResolutionRef,
    layerLinetypeMapRef,
    linetypeSysvarsRef,
  };
}
